import React, { useMemo, useCallback } from 'react'
import { useSelector } from 'react-redux'
import { withRouter } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Row, Col, Spin, Select, DatePicker } from 'antd'
import { useAiCreditCosts, useAiCostOrgSummary } from './hooks'
import TopCostUsers from './components/CostReport/TopCostUsers'
import SuperadminCostSummary from './components/CostReport/SuperadminCostSummary'
import SuperadminUsageByService from './components/CostReport/SuperadminUsageByService'
import SuperadminCostTrend from './components/CostReport/SuperadminCostTrend'
import ErrorState from './components/ErrorState'
import Unauthorized from '../Training/View/pages/Unauthorized'
import './AiCreditCostReport.scss'
import NewHeader from '../../components/NewHeader'
import ChevronRightIcon from '../../assets/icons/chevron-right-icon.svg'
import ReportsIcon from '../../assets/icons/reports-icon.svg'
import moment from 'moment'

const { Option } = Select
const { RangePicker } = DatePicker

const AiCreditCostReport = ({ history }) => {
  const { t } = useTranslation()
  const user = useSelector(state => state.user)
  const defaultOrgId = user?.info?.active_organization_id

  // Costs table state
  const {
    tableFilter: costTableFilter,
    setTableFilter: setCostTableFilter,
    dateFrom,
    setDateFrom,
    dateTo,
    setDateTo,
    userData,
    userLoading,
    userError,
    pagination,
    searchTerm,
    onSearch,
    onPageChange,
    refetchUsers,
    selectedType,
    setSelectedType,
    selectedOrg,
    setSelectedOrg,
    orgList,
    setDateRange
  } = useAiCreditCosts('5', defaultOrgId)

  // Superadmin org-level summary (filtered by same org + type + date)
  const {
    data: summaryData,
    loading: summaryLoading,
    error: summaryError,
    refetch: refetchSummary
  } = useAiCostOrgSummary({
    tableFilter: costTableFilter,
    dateFrom,
    dateTo,
    selectedOrg
  })

  const activeTypes = useMemo(() => [
    'ai-image',
    'ai_video',
    'audio',
    'course',
    'podcast',
    'maze',
    'sharebot'
  ], [])

  const loading = userLoading || summaryLoading
  const error = userError || summaryError

  const isTableActionLoading = React.useRef(false)

  React.useEffect(() => {
    if (!userLoading) {
      isTableActionLoading.current = false
    }
  }, [userLoading])

  const refetch = useCallback(() => {
    refetchUsers()
    refetchSummary()
  }, [refetchUsers, refetchSummary])

  const formatModelName = (modelString) => {
    if (!modelString) return ''
    return modelString
      .replace(/-/g, ' ')
      .replace(/\bpreview\b/gi, '')
      .replace(/\blatest\b/gi, '')
      .trim()
      .split(/\s+/)
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ')
  }

  const handleCostsExportClick = useCallback(() => {
    if (!userData || userData.length === 0) return

    const isSharebot = selectedType === 'sharebot'

    let avgCostTitle = 'Average Cost'
    if (selectedType === 'ai-image') avgCostTitle = 'Average Cost (per image)'
    else if (selectedType === 'ai_video') avgCostTitle = 'Average Cost (per video)'
    else if (selectedType === 'podcast') avgCostTitle = 'Average Cost (per second)'
    else if (selectedType === 'maze') avgCostTitle = 'Average Cost (per depth)'
    else if (selectedType === 'course') avgCostTitle = 'Average Cost (per page)'
    else if (selectedType === 'sharebot') avgCostTitle = 'Average Cost (per question)'

    const header = ['Rank', 'User', 'Email']
    if (!isSharebot) {
      header.push(selectedType === 'ai-image' ? 'Model Used' : 'Title')
    }
    header.push('Organization')
    if (selectedType === 'podcast') header.push('Duration (seconds)')
    if (selectedType === 'course') header.push('Total Pages')
    if (selectedType === 'maze') header.push('Depth')
    header.push(avgCostTitle, 'Total Credit Cost', 'Total Cost')
    if (selectedType === 'course') header.push('Elearning Course')

    const rows = userData.map((row, index) => {
      const rank = (pagination?.current - 1) * pagination?.pageSize + index + 1
      const rowData = [rank, row.name || 'Unknown User', row.email || '']
      if (!isSharebot) {
        rowData.push(selectedType === 'ai-image' ? formatModelName(row.model) : (row.title || ''))
      }
      rowData.push(row.organization)
      if (selectedType === 'podcast') rowData.push(row.duration)
      if (selectedType === 'course') rowData.push(row.totalPages)
      if (selectedType === 'maze') rowData.push(row.depth)
      rowData.push(row.totalCreditsUsed || 0)
      rowData.push(row.total_credit_cost || 0)
      rowData.push(row.totalCost || 0)
      if (selectedType === 'course') rowData.push(row.elearningCourse)
      return rowData
    })

    const csvContent = [header, ...rows]
      .map(e => e.map(v => `"${String(v).replace(/"/g, '""')}"`).join(','))
      .join('\n')

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    const fileName = `ai_cost_usage_${selectedType || 'report'}_${moment().format('YYYY-MM-DD')}.csv`
    a.href = url
    a.download = fileName
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }, [userData, pagination, selectedType])

  const handleRetry = useMemo(() => () => { refetch() }, [refetch])

  const viewState = useMemo(() => {
    if (loading && (!userData || userData.length === 0) && !summaryData) return 'loading'
    if (error && (!userData || userData.length === 0) && !summaryData) return 'error'
    return 'success'
  }, [userData, summaryData, error, loading])

  const renderContent = () => {
    if (viewState === 'loading' && !isTableActionLoading.current) {
      return (
        <div className="ai-credit-loading-container">
          <Spin size="large" />
        </div>
      )
    }

    if (viewState === 'error') {
      if (error && (String(error).includes('403') || String(error).toLowerCase().includes('access denied'))) {
        return <Unauthorized header={false} />
      }
      return <ErrorState error={error} onRetry={handleRetry} />
    }

    return (
      <>
        {/* Dashboard summary row */}
        <Row gutter={[24, 24]}>
          <Col span={24}>
            <div>
              <h3 className="org-title">Organization Cost Overview</h3>
            </div>
          </Col>
          <Col xs={24} sm={24} md={24} lg={24} xl={12} style={{ display: 'flex' }}>
            <div style={{ width: '100%' }}>
              <SuperadminCostSummary
                data={summaryData}
                loading={summaryLoading}
              />
            </div>
          </Col>
          <Col xs={24} sm={24} md={24} lg={24} xl={12} style={{ display: 'flex' }}>
            <div style={{ width: '100%' }}>
              <SuperadminUsageByService
                data={summaryData}
                loading={summaryLoading}
              />
            </div>
          </Col>
        </Row>

        {/* Trend chart row */}
        {/* <Row gutter={[24, 24]}>
          <Col span={24}>
            <SuperadminCostTrend
              data={summaryData}
              loading={summaryLoading}
            />
          </Col>
        </Row> */}

        {/* Cost report table */}
        <Row gutter={[24, 24]}>
          <Col span={24}>
            <TopCostUsers
              title="Cost Report"
              data={{ topUsers: userData || [] }}
              loading={userLoading}
              onExportClick={handleCostsExportClick}
              onSearch={(term) => {
                isTableActionLoading.current = true
                onSearch(term)
              }}
              searchTerm={searchTerm}
              pagination={pagination}
              onPageChange={(page, pageSize) => {
                isTableActionLoading.current = true
                onPageChange(page, pageSize)
              }}
              tableFilter={costTableFilter}
              onTableFilterChange={(filter) => {
                isTableActionLoading.current = true
                setCostTableFilter(filter)
              }}
              activeTypes={activeTypes}
              selectedType={selectedType}
              onTypeChange={(type) => {
                isTableActionLoading.current = true
                setSelectedType(type)
              }}
              orgList={orgList}
              selectedOrg={selectedOrg}
              onOrgChange={(org) => {
                isTableActionLoading.current = true
                setSelectedOrg(org)
              }}
            />
          </Col>
        </Row>
      </>
    )
  }

  const goBack = () => {
    history.push('/reports')
  }

  return (
    <div className="ai-credit-usage-report">
      <div className="ai-credit-nav-header">
        <div className="ai-credit-usage-report-breadcrumbs">
          <button className="breadcrumb-home" onClick={() => goBack()}>
            <img src={ReportsIcon} alt="Home" width="20" height="20" />
            Reports
          </button>
          <span className="breadcrumb-sep">
            <img src={ChevronRightIcon} alt=">" width="24" height="24" />
          </span>
          <span className="breadcrumb-settings">
            Ai Cost Usage
          </span>
        </div>
        <div className="ai-credit-usage-report-newheader">
          <NewHeader />
        </div>
      </div>

      <div className="report-header">
        <div className="header-content">
          <div className="title-section">
            <h1 className="main-title">AI Cost Usage</h1>
            <p className="subtitle">
              Real-time visibility into AI cost usage — credits consumed and their dollar value
            </p>
          </div>
          <div className="filters-section" style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
            {costTableFilter === 'Other' && (
              <RangePicker
                size="medium"
                format="YYYY-MM-DD"
                placeholder={[t('reports:from_date'), t('reports:to_date')]}
                showTime={false}
                value={[dateFrom ? moment(dateFrom) : null, dateTo ? moment(dateTo) : null]}
                onChange={(dates, dateStrings) => {
                  isTableActionLoading.current = false
                  setDateFrom(dateStrings[0])
                  setDateTo(dateStrings[1])
                  if (setDateRange) setDateRange(dates, dateStrings)
                }}
              />
            )}
            <Select
              className="time-filter-select"
              size="large"
              value={costTableFilter}
              onChange={(value) => {
                isTableActionLoading.current = false
                setCostTableFilter(value)
                if (value !== 'Other') {
                  setDateFrom('')
                  setDateTo('')
                }
              }}
              style={{ width: 150 }}
            >
              <Option value="All Time">{t('reports:all_time')}</Option>
              <Option value="Today">{t('reports:today')}</Option>
              <Option value="This Week">{t('reports:seven_days')}</Option>
              <Option value="This Month">{t('reports:last_month')}</Option>
              <Option value="This Year">{t('reports:one_year')}</Option>
              <Option value="Other">{t('v4:others')}</Option>
            </Select>
            <Select
              showSearch
              allowClear
              placeholder="All Organizations"
              optionFilterProp="children"
              value={selectedOrg || undefined}
              onChange={(val) => {
                isTableActionLoading.current = false
                setSelectedOrg(val)
              }}
              filterOption={(input, option) => {
                const child = option.props.children;
                return child && String(child).toLowerCase().includes(input.toLowerCase());
              }}
              style={{ width: 200 }}
              size="large"
            >
              {orgList?.map(org => (
                <Option key={org.id} value={org.id}>{org.title || org.name}</Option>
              ))}
            </Select>
          </div>
        </div>
      </div>

      <div className="report-body">
        {renderContent()}
      </div>
    </div>
  )
}

export default withRouter(AiCreditCostReport)
